import {Errors} from "~/components/DataBaseComponent";
import {BusyState} from "~/components/BusyFromPromise/BusyFromPromise";
import {HostedInstanceResource} from "~/client/resources/hostedInstanceResource";
import {CleanUpStrategyType, LimitedLifetimeCleanUpStrategy} from "~/client/resources/cleanUpStrategy";
import {ChangeCleanUpStrategyRequest} from "~/client/resources/changeCleanUpStrategyRequest";
import repository from "~/client/repository";
import React, {useState} from "react";
import SaveDialogLayout from "~/components/DialogLayout/SaveDialogLayout";
import {BooleanRadioButton, BooleanRadioButtonGroup} from "@octopusdeploy/design-system-components";
import { Callout } from "@octopusdeploy/design-system-components";
import {DoBusyTask} from "~/components/DataBaseComponent/DataBaseComponent";

interface ChangeCleanUpStrategyDialogProps {
    errors: Errors;
    busy: BusyState;
    instance: HostedInstanceResource;
    setInstance: (instance: HostedInstanceResource) => void;
    doBusyTask: DoBusyTask;
}

function ChangeCleanUpStrategyDialog({ instance, setInstance, errors, busy, doBusyTask}: ChangeCleanUpStrategyDialogProps) {
    const setToNeverDeactivate = () => {
        return doBusyTask(async () => {
            const limitedLifetimeCleanUpStrategy = instance.CleanUpStrategy as LimitedLifetimeCleanUpStrategy;
            const request: ChangeCleanUpStrategyRequest = {
                CleanUpStrategy: {
                    CleanUpStrategyType:CleanUpStrategyType.NeverCleanUpStrategy,
                    CreatedOn: limitedLifetimeCleanUpStrategy.CreatedOn
                }
            }
            const savedInstance = await repository.HostedInstances.changeCleanUpStrategy({ instance, request });
            setInstance(savedInstance);
            return true;
        });
    }

    const extendInstanceLifetime = () => {
        return doBusyTask(async () => {
            const savedInstance = await repository.HostedInstances.extendLifetime(instance);
            setInstance(savedInstance);
            return true;
        });
    }

    const deactivationInDays = (instance.CleanUpStrategy as LimitedLifetimeCleanUpStrategy).DeactivationInDays;

    const [shouldSetToNeverDeactivate, setShouldSetToNeverDeactivate] = useState<boolean>(false);
    const saveClickAction = shouldSetToNeverDeactivate ? setToNeverDeactivate : extendInstanceLifetime;
    const actionCallout = shouldSetToNeverDeactivate
        ? (
            <Callout type="warning" title="Warning">
                Setting a branch Instance to never deactivate will require you to manually delete this Instance when you are finished.
                Consider cost implications when using this feature.
            </Callout>
        )
        : (
            <Callout type="information" title="Information">
                Extending a branch Instance will delay the deactivation and deletion dates by {deactivationInDays} days.
            </Callout>
        )

    return <SaveDialogLayout title={"Change Instance Lifetime?"}
                             busy={busy}
                             errors={errors}
                             onSaveClick={saveClickAction}>

        {actionCallout}

        <BooleanRadioButtonGroup value={shouldSetToNeverDeactivate} onChange={setShouldSetToNeverDeactivate}>
            <BooleanRadioButton value={false} label={`Extend for ${deactivationInDays} Days`} />
            <BooleanRadioButton value={true} label="Set to Never Deactivate" />
        </BooleanRadioButtonGroup>
    </SaveDialogLayout>;
}

export default ChangeCleanUpStrategyDialog;
